import { useState } from "react";
import { REGISTER_PAGE } from "../../utils/url/staticUrls";
import { useBooleanState } from "../../common/hooks/useBooleanState";
import LoginPopup from "../../Login/components/LoginPopup";
import ResetPasswordPopup from "../../Login/components/ResetPasswordPopup";
import { trackRegisterInAffiliate } from "../../feature/Analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/feature/Affiliate/redux/selectors";
import { useSelector } from "react-redux";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import { Button } from "~/common/components/Button/Button";
import { trackPartnerFinderSearch } from "~/feature/Analytics/ga/salesPartner";
import { useFormContext } from "react-hook-form";
import type { SASearchFormFields } from "./SearchCard/SalesPartnerSearchBox";
import type { GoogleMarker } from "../salesPartnerTypes";
import { transformSelectedMarkerData } from "~/feature/cart/B2CRequestPage/utils";

type Props = {
  open?: boolean;
  onClose?: () => void;
  selectedMarker: GoogleMarker | undefined;
};
export const RegisterToSaveSnackbar = (props: Props) => {
  const { t } = useTranslation();

  const { openLogin, component } = useLoginPopup();

  const savedAffiliatePartner = useSelector(selectSavedAffiliate);
  const visitedAffiliatePartner = useSelector(selectVisitedAffiliate);
  const { getValues } = useFormContext<SASearchFormFields>();

  const onClickRegister = () => {
    trackSignInOrRegister(true);
    const bannerName = savedAffiliatePartner?.name ?? visitedAffiliatePartner?.name ?? "";
    trackRegisterInAffiliate(bannerName);
  };

  const trackSignInOrRegister = (isRegister: boolean) => {
    trackPartnerFinderSearch(
      getValues(),
      isRegister ? "Register Intended" : "Sign In Intended",
      "Click",
      transformSelectedMarkerData(props.selectedMarker),
    );
  };

  return (
    <>
      <Snackbar
        {...props}
        onClose={props.onClose}
        message={t("page.salespartner.snackbar.register.label")}
        action={
          <>
            <Button
              color="inversePrimary"
              variant={"text"}
              size="medium"
              onClick={() => {
                trackSignInOrRegister(false);
                openLogin();
              }}
            >
              {t("header.link.signIn")}
            </Button>
            <em> {t("common.or")} </em>
            <Button
              color="inversePrimary"
              variant={"text"}
              size="medium"
              href={`${REGISTER_PAGE}?ref=${window.location.href}`}
              onClick={onClickRegister}
            >
              {t("header.link.register")}
            </Button>
            <SnackbarCloseButton onClick={props.onClose} />
          </>
        }
      />
      {component}
    </>
  );
};

interface UseLoginProps {
  onCloseLogin?: () => void;
  alternativeGAAction?: string;
}

const useLoginPopup = (props?: UseLoginProps) => {
  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);

  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const onCloseLogin = () => {
    closeLogin();
    props?.onCloseLogin?.();
  };

  const component = (
    <>
      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={onResetClick}
          close={onCloseLogin}
          alternativeGAAction={props?.alternativeGAAction}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
    </>
  );

  return {
    openLogin,
    openReset,
    component,
  };
};
